import { useState } from 'react';
import type { TestReview, Verdict } from '@sr-test-wizard/shared';
import { VERDICT_CSS } from '@sr-test-wizard/shared';
import { api } from '../../lib/api';
import { useRunStore } from '../../state/runStore';

export type VerdictChip = 'tba' | 'in-game' | 'pass' | 'fail' | 'na';

interface Props {
  runId: string;
  tests: TestReview[];
  currentIndex: number;
  onSelect: (index: number) => void;
  filter: string;
  onFilter: (v: string) => void;
  chips: VerdictChip[];
  onToggleChip: (chip: VerdictChip) => void;
}

function dotClass(verdict: Verdict | null): string {
  if (!verdict) return 'empty';
  return VERDICT_CSS[verdict];
}

export function effectiveVerdict(t: TestReview): Verdict | null {
  return t.tester_verdict ?? t.ai_verdict ?? null;
}

export function chipForVerdict(v: Verdict | null): VerdictChip {
  if (!v) return 'tba';
  const css = VERDICT_CSS[v];
  if (css === 'in-game') return 'in-game';
  if (css === 'pass') return 'pass';
  if (css === 'fail') return 'fail';
  return 'na';
}

export function filterTests(tests: TestReview[], filter: string, chips: VerdictChip[]): TestReview[] {
  const norm = filter.trim().toLowerCase();
  return tests.filter((t) => {
    if (norm) {
      const hit =
        t.test_key.toLowerCase().includes(norm) || t.test_summary.toLowerCase().includes(norm);
      if (!hit) return false;
    }
    if (chips.length > 0 && !chips.includes(chipForVerdict(effectiveVerdict(t)))) return false;
    return true;
  });
}

export function SidebarTestsList({
  runId,
  tests,
  currentIndex,
  onSelect,
  filter,
  onFilter,
  chips,
  onToggleChip,
}: Props) {
  const analyzingTestKeys = useRunStore((s) => s.analyzingTestKeys);
  const setTestAnalyzing = useRunStore((s) => s.setTestAnalyzing);
  const analysisInputs = useRunStore((s) => s.analysisInputs);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [resettingKey, setResettingKey] = useState<string | null>(null);
  const analyzingSet = new Set(analyzingTestKeys);

  const analyzeOne = async (testKey: string) => {
    setPendingKey(testKey);
    setTestAnalyzing(testKey, true);
    try {
      await api.post(`/api/runs/${runId}/tests/${testKey}/analyze`, {
        ...(analysisInputs ? { inputs: analysisInputs } : {}),
      });
    } catch {
      setTestAnalyzing(testKey, false);
    } finally {
      setPendingKey((current) => (current === testKey ? null : current));
    }
  };

  const resetOne = async (testKey: string) => {
    if (!window.confirm(`Clear AI/tester/play results for ${testKey}? Jira status is kept.`)) return;
    setResettingKey(testKey);
    try {
      await api.post(`/api/runs/${runId}/tests/reset`, { testKeys: [testKey] });
    } finally {
      setResettingKey(null);
    }
  };

  const filtered = filterTests(tests, filter, chips);

  const summary = tests.reduce(
    (acc, t) => {
      const v = effectiveVerdict(t);
      if (!v) acc.unanalyzed++;
      else acc[VERDICT_CSS[v] as keyof typeof acc]++;
      return acc;
    },
    { pass: 0, fail: 0, 'in-game': 0, na: 0, unanalyzed: 0 } as Record<string, number>,
  );

  const chipBtn = (id: VerdictChip, label: string, count: number, className: string) => (
    <button
      type="button"
      className={`badge ${className} filter-chip${chips.includes(id) ? ' is-on' : ''}`}
      onClick={() => onToggleChip(id)}
      title={`Show ${label} tests`}
    >
      {label} {count}
    </button>
  );

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <input
          placeholder="Filter by key or summary"
          value={filter}
          onChange={(e) => onFilter(e.target.value)}
        />
        <div className="row small muted" style={{ flexWrap: 'nowrap', gap: 4, overflowX: 'auto' }}>
          {chipBtn('tba', 'TBA', summary.unanalyzed, '')}
          {chipBtn('in-game', 'In-Game', summary['in-game'], 'in-game')}
          {chipBtn('pass', 'Pass', summary.pass, 'good')}
          {chipBtn('fail', 'Fail', summary.fail, 'bad')}
          {chipBtn('na', 'N/A', summary.na, 'grey')}
        </div>
      </div>
      <div className="sidebar-list">
        {filtered.length === 0 && (
          <p className="muted small" style={{ padding: 12 }}>
            No tests match the filter.
          </p>
        )}
        {filtered.map((test) => {
          const idx = tests.findIndex((t) => t.test_key === test.test_key);
          const verdict = effectiveVerdict(test);
          const isActive = idx === currentIndex;
          const hasAi = !!test.ai_verdict || !!test.tester_verdict || !!test.in_game_play;
          const isAnalyzing = analyzingSet.has(test.test_key) || pendingKey === test.test_key;
          return (
            <div
              key={test.test_key}
              className={`test-item status-${dotClass(verdict)} ${isActive ? 'active' : ''} ${isAnalyzing ? 'analyzing' : ''}`}
              onClick={() => onSelect(idx)}
            >
              {isAnalyzing ? (
                <span
                  className="test-item-spinner"
                  title="AI analysis in progress"
                  aria-label="analyzing"
                />
              ) : (
                <span className={`dot ${dotClass(verdict)}`} title={verdict ?? 'not analyzed'} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="test-key">{test.test_key}</div>
                <div className="test-summary" title={test.test_summary}>
                  {test.test_summary}
                </div>
                <div className="test-meta">
                  {isAnalyzing && (
                    <span className="badge analyzing small" title="AI analysis in progress">
                      Analyzing...
                    </span>
                  )}
                  {test.xray_status && <span className="badge small">{test.xray_status}</span>}
                  {test.bug_key && <span className="badge bug small">{test.bug_key}</span>}
                  {test.tester_verdict && test.tester_verdict !== test.ai_verdict && (
                    <span className="badge info small" title="Tester override">
                      override
                    </span>
                  )}
                </div>
              </div>
              {hasAi && !isAnalyzing && (
                <button
                  type="button"
                  className="test-item-reset"
                  title="Reset this test to Jira status"
                  disabled={resettingKey === test.test_key}
                  onClick={(e) => {
                    e.stopPropagation();
                    void resetOne(test.test_key);
                  }}
                >
                  {resettingKey === test.test_key ? '…' : 'Reset'}
                </button>
              )}
              <button
                type="button"
                className="test-item-analyze"
                title={hasAi ? 'Re-run AI for this test' : 'Run AI for this test'}
                disabled={isAnalyzing}
                onClick={(e) => {
                  e.stopPropagation();
                  void analyzeOne(test.test_key);
                }}
              >
                {isAnalyzing ? '...' : test.ai_verdict ? '\u21BB' : 'AI'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
